import React from 'react';
import { Building, TrendingUp } from 'lucide-react';
import { Supplier } from '@app-types';
import { formatInstantAsDate } from '@utils/dateUtils';
import { tierToVariant } from '@utils/badgeMappers';
import Badge from '@components/common/Badge';
import TierBadge from '@components/common/TierBadge';
import { supplierStatusToVariant } from '@utils/badgeMappers';
import { getSupplierStatusLabel } from '@utils/referenceCatalog';
import { InfoCard, InfoCardGrid, StatsGrid } from '@components/common';
import styles from './SupplierInfo.module.css';

interface SupplierInfoProps {
  supplier: Supplier;
  contractsCount?: number;
  assessmentsCount?: number;
  hideAuditMetadata?: boolean;
}

const SupplierInfo: React.FC<SupplierInfoProps> = ({
  supplier,
  contractsCount = 0,
  assessmentsCount = 0,
  hideAuditMetadata = false
}) => {
  return (
    <div className={styles.supplierInfoSimple}>
      <InfoCardGrid>
        {/* Basic Info Card */}
        <InfoCard>
          <InfoCard.Header>
            <InfoCard.Title icon={<Building size={20} />}>Supplier Information</InfoCard.Title>
          </InfoCard.Header>
          <InfoCard.Content>
            <InfoCard.Row>
              <InfoCard.Label>Name:</InfoCard.Label>
              <InfoCard.Value>{supplier.name}</InfoCard.Value>
            </InfoCard.Row>
            {supplier.vendor_id && (
              <InfoCard.Row>
                <InfoCard.Label>Supplier Number:</InfoCard.Label>
                <InfoCard.Value>{supplier.vendor_id}</InfoCard.Value>
              </InfoCard.Row>
            )}
            <InfoCard.Row>
              <InfoCard.Label>Status:</InfoCard.Label>
              <Badge variant={supplierStatusToVariant(supplier.status)}>{getSupplierStatusLabel(supplier.status)}</Badge>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Tier:</InfoCard.Label>
              <TierBadge variant={tierToVariant(supplier.tier_name)}>{supplier.tier_name}</TierBadge>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>High Trust Supplier:</InfoCard.Label>
              <Badge variant={supplier.high_trust_supplier ? 'success' : 'default'}>
                {supplier.high_trust_supplier ? 'Yes' : 'No'}
              </Badge>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Critical Service/Product:</InfoCard.Label>
              <Badge variant={supplier.critical_service_product ? 'success' : 'default'}>
                {supplier.critical_service_product ? 'Yes' : 'No'}
              </Badge>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Additional Data 1:</InfoCard.Label>
              <InfoCard.Value>{supplier.additional_data_1 || '—'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Additional Data 2:</InfoCard.Label>
              <InfoCard.Value>{supplier.additional_data_2 || '—'}</InfoCard.Value>
            </InfoCard.Row>
          </InfoCard.Content>

          {/* Audit Metadata - Hidden for CO/CM users */}
          {!hideAuditMetadata && (
            <InfoCard.Metadata>
              {supplier.created_by_name && supplier.created_at && (
                <InfoCard.MetadataText>
                  Created by {supplier.created_by_name} on {formatInstantAsDate(supplier.created_at)}
                </InfoCard.MetadataText>
              )}
              {supplier.updated_by_name && supplier.updated_at && (
                <InfoCard.MetadataText>
                  Modified by {supplier.updated_by_name} on {formatInstantAsDate(supplier.updated_at)}
                </InfoCard.MetadataText>
              )}
            </InfoCard.Metadata>
          )}
        </InfoCard>

        {/* Statistics Card */}
        <InfoCard>
          <InfoCard.Header>
            <InfoCard.Title icon={<TrendingUp size={20} />}>Statistics</InfoCard.Title>
          </InfoCard.Header>
          <InfoCard.Content>
            <StatsGrid>
              <StatsGrid.Item>
                <StatsGrid.Number>{contractsCount}</StatsGrid.Number>
                <StatsGrid.Label>Contracts</StatsGrid.Label>
              </StatsGrid.Item>
              <StatsGrid.Item>
                <StatsGrid.Number>{assessmentsCount}</StatsGrid.Number>
                <StatsGrid.Label>Assessments</StatsGrid.Label>
              </StatsGrid.Item>
            </StatsGrid>
          </InfoCard.Content>
        </InfoCard>
      </InfoCardGrid>
    </div>
  );
};

export default SupplierInfo;
